/**
 * Cross-cutting fixed vocabularies — the TS mirror of `api/schemas/common.py`'s
 * `Literal` types and conventions.md §2.4.
 *
 * These are string-literal unions, byte-identical to the backend's wire `Literal`s. The
 * `EdgeType` union is the single frontend source of truth for the nine PRD Appendix C
 * edge types (conventions.md §2.4 — "MCP and frontend import it, never re-declare").
 */

/** Note provenance — conventions.md §2.4. `chat` is chat-promoted notes; `plaud` is v1.5. */
export type SourceType =
  | 'joplin'
  | 'manual'
  | 'paste'
  | 'markdown_import'
  | 'chat'
  | 'plaud';

/** Entity kinds — mirrors `EntityTypeName`. */
export type EntityTypeName =
  | 'person'
  | 'organization'
  | 'project'
  | 'location'
  | 'date'
  | 'mentioned_id';

/** Action-item lifecycle — mirrors `ActionStatus`. */
export type ActionStatus = 'open' | 'resolved' | 'dropped';

/** The two edge classes (D-005) — mirrors `EdgeClassName`. */
export type EdgeClass = 'materialized' | 'derived';

/**
 * The nine edge types (PRD Appendix C) — mirrors `EdgeTypeName`. The single frontend
 * source of truth; no surface re-declares this list (conventions.md §2.4).
 */
export type EdgeType =
  | 'references'
  | 'extends'
  | 'contradicts'
  | 'supersedes'
  | 'responds_to'
  | 'supports'
  | 'refines'
  | 'shares_entity'
  | 'shares_hashtag'
  | 'semantically_similar'
  | 'cooccurs_temporally';

/** All edge types as a runtime array — for filter UIs that enumerate them. */
export const EDGE_TYPES: readonly EdgeType[] = [
  'references',
  'extends',
  'contradicts',
  'supersedes',
  'responds_to',
  'supports',
  'refines',
  'shares_entity',
  'shares_hashtag',
  'semantically_similar',
  'cooccurs_temporally',
] as const;

/** Time-series bucketing granularity — mirrors `Granularity`. */
export type Granularity = 'day' | 'week' | 'month';

/** Per-note extraction status — mirrors `ExtractionStatus`. */
export type ExtractionStatus =
  | 'queued'
  | 'stage_a'
  | 'stage_c'
  | 'stage_d'
  | 'complete'
  | 'failed';

/** Extraction stages (D-006) — single uppercase chars, matching the queue rows. */
export type ExtractionStage = 'A' | 'C' | 'D';

/** The PRD §13.3 error categories — mirrors the `ErrorEnvelope.category` field. */
export type ErrorCategory = 'user' | 'system' | 'external';

// `CapabilityTier` removed alongside the local ONNX embedding pipeline
// (2026-05-17). Render budgets are now static defaults in
// `src/config/renderDefaults.ts`.
